// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the small colour dialog a real-time overlay's palette button
 * opens (owner, 2026-10-09): the palette and the min/max of its colours,
 * "nothing more, nothing less". No thresholds, no reset: those stay in
 * Tools → View for the dataset. AYNI (owner, 2026-10-20): a small Invert
 * box beside the palette's label turns the colours round.
 *
 *   ▬ Air Quality (PM2.5)                 ✕
 *   Palette                      ☐ Invert
 *   [Original] [Viridis] [Magma]
 *   [Inferno ] [Plasma ] [Cividis]
 *   [Turbo   ] [Cool–warm] [Grayscale]
 *   Minimum [ 0    µg/m³ ]  Maximum [ 150  µg/m³ ]
 *   ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
 *   Transparency       30 %
 *   ■ ━━━━●━━━━━━━━ □               [Done]
 *
 * AYNI (owner, 2026-10-12): the transparency beside Done, for every
 * layer, from fully opaque (left) to fully transparent (right). A layer
 * whose colours cannot change (a picture, or classes) opens the same
 * dialog with a line saying so in place of the palette and the limits,
 * so its transparency is still in reach.
 *
 * Every change goes straight to the globe (`onChange`), as in Tools →
 * View, so there is nothing to apply. A minimum at or above the maximum
 * is refused with a line saying so, and the fields go back to the live
 * limits when they lose focus. Done, ✕, Escape or a press outside closes
 * it, and focus goes back to the button that opened it.
 *
 * One dialog at a time. It is fixed to the viewport beside its button,
 * on the side with more room, so it works where the panel sits on the
 * right (left-to-right) or the left (right-to-left), and in the phone's
 * AR overlay, which passes its own `container` (only that subtree is
 * drawn over the camera).
 *
 * AYNI (owner, 2026-10-20): the change map in Analyze opens it too, for its
 * palette, Invert and range. Its colours are not a dataset's, so it passes
 * a `ramp` (how to draw a palette, read and set the limits, and why typed
 * limits are refused) instead of a scale; and as the panel redraws its
 * button, `setAnchor` moves the open dialog onto the new one.
 */

import { t, tAttr, tHtml, type MessageKey } from '../i18n'
import { DEFAULT_DISPLAY, PALETTE_IDS, type ColorScaleDisplay, type PaletteId } from '../services/colorScaleDisplay'
import { formatLimit, formatTransparency, limitsOf, rampGradient, transparencyPercent, withPaletteAndLimits } from '../services/overlayDisplay'
import type { ColorScale } from '../types/color-scale'
import { escapeAttr, escapeHtml } from './domUtils'

/** AYNI: colours that are not a dataset's (the change map): drawn, read and set through these instead of a scale. */
export interface PaletteRamp {
  /** The palettes offered (default: every one). */
  readonly palettes?: readonly PaletteId[]
  /** A palette under a display as a CSS gradient. */
  gradient(display: ColorScaleDisplay, direction: string): string
  /** The limits drawn now. */
  limits(): { min: number; max: number }
  /** Apply typed limits (minimum below maximum); false refuses them and the dialog says `invalid`. */
  setLimits(min: number, max: number): boolean
  /** Why typed limits were refused (default: the minimum must be below the maximum). */
  readonly invalid?: string
  readonly units?: string
}

export interface PaletteDialogOptions {
  /** The button that opened it: it is placed beside it, and focus goes back to it. */
  anchor: HTMLElement
  /** Where to mount it (default: the page body). */
  container?: HTMLElement
  /** A panel to open beside rather than over (the playback panel), when the screen has room. */
  beside?: HTMLElement | null
  /** The layer's name, the dialog's heading. */
  title: string
  /** Yellow for an overlay, green for the dataset. */
  accent: 'overlay' | 'base'
  /** Null when the layer's colours cannot change: `fixedReason` is shown instead of the palette and the limits (unless `ramp` is given). */
  scale: ColorScale | null
  /** AYNI: colours that are not a dataset's; used with a null `scale`. */
  ramp?: PaletteRamp
  /** Why the colours cannot change (with a null `scale`). */
  fixedReason?: string
  display: ColorScaleDisplay
  onChange(next: ColorScaleDisplay): void
  /** AYNI: the layer's opacity now, 1 opaque … 0 transparent (default 1). */
  opacity?: number
  /** AYNI: called as the transparency slider moves; without it there is no slider. */
  onOpacityChange?(opacity: number): void
  onClose?(): void
}

export interface PaletteDialogHandle {
  readonly element: HTMLElement
  readonly anchor: HTMLElement
  /** AYNI: the panel redrew its button: sit beside the new one (with its `ramp`), and read the colours and limits again. */
  setAnchor(anchor: HTMLElement, ramp?: PaletteRamp): void
  /** AYNI: show an opacity set elsewhere (a meeting's lead, the headset), unless the slider is held. */
  setOpacity(opacity: number): void
  close(): void
}

/** AYNI: the slider's step, in percent of transparency. */
const ALPHA_STEP = 5

export { formatTransparency, transparencyPercent }

/** Gap between the dialog and its button, and the viewport's edges. */
const GAP_PX = 10
const EDGE_PX = 8

let open: PaletteDialogHandle | null = null

/** The dialog open now, if any. */
export function currentPaletteDialog(): PaletteDialogHandle | null {
  return open
}

/** Close whichever dialog is open. */
export function closePaletteDialog(): void {
  open?.close()
}

/** Open the dialog, or close it when its button is pressed again. */
export function togglePaletteDialog(opts: PaletteDialogOptions): PaletteDialogHandle | null {
  if (open && open.anchor === opts.anchor) {
    open.close()
    return null
  }
  return openPaletteDialog(opts)
}

/** A limit as typed: three significant figures, no grouping. */
function limitText(value: number): string {
  if (!Number.isFinite(value)) return ''
  return String(Number(value.toPrecision(3)))
}

export function openPaletteDialog(opts: PaletteDialogOptions): PaletteDialogHandle {
  open?.close()
  const { scale } = opts
  let ramp = opts.ramp
  let anchor = opts.anchor
  let display = opts.display
  const rawUnits = scale?.units ?? ramp?.units ?? ''
  const units = rawUnits ? escapeHtml(rawUnits) : ''
  const el = document.createElement('div')
  el.className = 'ovl-palette'
  el.dataset.accent = opts.accent
  el.setAttribute('role', 'dialog')
  el.setAttribute('aria-modal', 'false')
  el.setAttribute('aria-labelledby', 'ovl-palette-title')
  const unitSpan = units ? `<span class="ovl-palette-unit">${units}</span>` : ''
  const colours = scale || ramp
    ? `
    <div class="ovl-palette-phead">
      <div class="ovl-palette-label" id="ovl-palette-plabel">${tHtml('colorbar.palette.label')}</div>
      <label class="ovl-palette-invert"><input type="checkbox" id="ovl-palette-invert">${tHtml('palette.dialog.invert')}</label>
    </div>
    <div class="ovl-palette-chips" role="group" aria-labelledby="ovl-palette-plabel">
      ${(ramp?.palettes ?? PALETTE_IDS).map((id) => `
        <button type="button" class="ovl-palette-chip" data-palette="${escapeAttr(id)}" aria-pressed="false">
          <span class="ovl-palette-chip-bar" aria-hidden="true"></span>
          <span class="ovl-palette-chip-name">${tHtml(`colorbar.palette.${id}` as MessageKey)}</span>
        </button>`).join('')}
    </div>
    <div class="ovl-palette-limits">
      <label class="ovl-palette-field-label" for="ovl-palette-min">${tHtml('palette.dialog.min')}
        <span class="ovl-palette-field"><input type="number" id="ovl-palette-min" step="any" inputmode="decimal">${unitSpan}</span>
      </label>
      <label class="ovl-palette-field-label" for="ovl-palette-max">${tHtml('palette.dialog.max')}
        <span class="ovl-palette-field"><input type="number" id="ovl-palette-max" step="any" inputmode="decimal">${unitSpan}</span>
      </label>
    </div>
    <p class="ovl-palette-error" role="alert" hidden>${ramp?.invalid ? escapeHtml(ramp.invalid) : tHtml('tools.palette.invalid')}</p>
    <div class="ovl-palette-preview" aria-hidden="true">
      <div class="ovl-palette-preview-bar"></div>
      <div class="ovl-palette-preview-ends"><span></span><span></span></div>
    </div>`
    : `
    <p class="ovl-palette-fixed">${escapeHtml(opts.fixedReason ?? '')}</p>`
  const alpha = opts.onOpacityChange
    ? `
      <div class="ovl-palette-alpha">
        <div class="ovl-palette-alpha-head">
          <label class="ovl-palette-label" for="ovl-palette-alpha">${tHtml('palette.dialog.transparency')}</label>
          <output class="ovl-palette-alpha-value" for="ovl-palette-alpha"></output>
        </div>
        <div class="ovl-palette-alpha-row">
          <span class="ovl-palette-alpha-end" data-end="opaque" title="${tAttr('palette.dialog.opaque')}" aria-hidden="true"></span>
          <input type="range" id="ovl-palette-alpha" class="ovl-palette-alpha-input" min="0" max="100" step="${ALPHA_STEP}">
          <span class="ovl-palette-alpha-end" data-end="clear" title="${tAttr('palette.dialog.clear')}" aria-hidden="true"></span>
        </div>
      </div>`
    : ''
  el.innerHTML = `
    <div class="ovl-palette-head">
      <span class="ovl-palette-mark" aria-hidden="true"></span>
      <h3 class="ovl-palette-title" id="ovl-palette-title">${escapeHtml(opts.title)}</h3>
      <button type="button" class="ovl-palette-x" aria-label="${tAttr('palette.dialog.close')}" title="${tAttr('palette.dialog.close')}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12"/></svg>
      </button>
    </div>
    ${colours}
    <div class="ovl-palette-foot">${alpha}
      <button type="button" class="ovl-palette-done">${tHtml('palette.dialog.done')}</button>
    </div>`

  // AYNI: the transparency, for every layer.
  const alphaInput = el.querySelector<HTMLInputElement>('#ovl-palette-alpha')
  const alphaValue = el.querySelector<HTMLOutputElement>('.ovl-palette-alpha-value')
  let opacity = opts.opacity ?? 1
  function syncAlpha(): void {
    if (!alphaInput) return
    const percent = transparencyPercent(opacity)
    const text = formatTransparency(percent)
    alphaInput.value = String(percent)
    alphaInput.setAttribute('aria-valuetext', text)
    if (alphaValue) alphaValue.textContent = text
  }
  alphaInput?.addEventListener('input', () => {
    const percent = Math.min(100, Math.max(0, Number(alphaInput.value) || 0))
    opacity = 1 - percent / 100
    syncAlpha()
    opts.onOpacityChange?.(opacity)
  })
  let alphaHeld = false
  alphaInput?.addEventListener('pointerdown', () => { alphaHeld = true })
  alphaInput?.addEventListener('pointerup', () => { alphaHeld = false })
  alphaInput?.addEventListener('pointercancel', () => { alphaHeld = false })

  if (!scale && !ramp) {
    // Nothing to colour: only the transparency and Done.
    return finish(() => {}, () => {})
  }
  // AYNI: a dataset's scale, or the change map's ramp.
  const gradientOf = (d: ColorScaleDisplay, direction: string): string => (ramp ? ramp.gradient(d, direction) : rampGradient(scale!, d, direction))
  const limitsNow = (): { min: number; max: number } => (ramp ? ramp.limits() : limitsOf(scale!, display))

  const minInput = el.querySelector<HTMLInputElement>('#ovl-palette-min')!
  const maxInput = el.querySelector<HTMLInputElement>('#ovl-palette-max')!
  const error = el.querySelector<HTMLElement>('.ovl-palette-error')!
  const invertInput = el.querySelector<HTMLInputElement>('#ovl-palette-invert')!
  const previewBar = el.querySelector<HTMLElement>('.ovl-palette-preview-bar')!
  const [endLo, endHi] = Array.from(el.querySelectorAll<HTMLElement>('.ovl-palette-preview-ends span'))

  // The chips' bars: each palette as it would draw this layer (the
  // dataset's own for Original), turned round when the display is.
  let barsInverted: boolean | null = null
  function syncChipBars(): void {
    const inverted = !!display.inverted
    if (inverted === barsInverted) return
    barsInverted = inverted
    for (const chip of el.querySelectorAll<HTMLElement>('.ovl-palette-chip')) {
      const palette = chip.dataset.palette as PaletteId
      const bar = chip.querySelector<HTMLElement>('.ovl-palette-chip-bar')!
      bar.style.background = gradientOf({ ...DEFAULT_DISPLAY, palette, inverted }, 'to right')
    }
  }

  // The preview's ends are for reading (0,5 in Spanish); the fields keep the digits a number field takes.
  const withUnits = (value: number): string => formatLimit(value, rawUnits || null)

  /** Bring the chips, the fields (unless being typed in) and the preview in line with the display. */
  function sync(): void {
    syncChipBars()
    invertInput.checked = !!display.inverted
    for (const chip of el.querySelectorAll<HTMLElement>('.ovl-palette-chip')) {
      chip.setAttribute('aria-pressed', String(chip.dataset.palette === display.palette))
    }
    const { min, max } = limitsNow()
    if (document.activeElement !== minInput) minInput.value = limitText(min)
    if (document.activeElement !== maxInput) maxInput.value = limitText(max)
    previewBar.style.background = gradientOf(display, 'to right')
    if (endLo) endLo.textContent = withUnits(min)
    if (endHi) endHi.textContent = withUnits(max)
  }

  function apply(next: ColorScaleDisplay): void {
    display = next
    opts.onChange(next)
    sync()
  }

  el.querySelector('.ovl-palette-chips')!.addEventListener('click', (ev) => {
    const chip = (ev.target as HTMLElement).closest<HTMLElement>('.ovl-palette-chip')
    if (!chip) return
    const palette = chip.dataset.palette as PaletteId
    if (palette !== display.palette) apply({ ...display, palette })
  })

  invertInput.addEventListener('change', () => apply({ ...display, inverted: invertInput.checked }))

  /** The typed limits, when they make a range. */
  function typed(): { min: number; max: number } | null {
    if (minInput.value.trim() === '' || maxInput.value.trim() === '') return null
    const min = Number(minInput.value)
    const max = Number(maxInput.value)
    return Number.isFinite(min) && Number.isFinite(max) && max > min ? { min, max } : null
  }

  // AYNI: a ramp may refuse limits a scale would take (the change map's must straddle zero).
  let refused = false
  const onLimitInput = (): void => {
    const range = typed()
    if (ramp) {
      refused = !range || !ramp.setLimits(range.min, range.max)
      error.hidden = !refused
      if (!refused) sync()
      return
    }
    error.hidden = !!range
    if (range) apply(withPaletteAndLimits(scale!, display, display.palette, range.min, range.max))
  }
  // Leaving a field with a range that does not work puts the live limits back.
  const onLimitBlur = (): void => {
    if (typed() && !refused) return
    setTimeout(() => {
      if (document.activeElement === minInput || document.activeElement === maxInput) return
      error.hidden = true
      sync()
    }, 0)
  }
  for (const input of [minInput, maxInput]) {
    input.addEventListener('input', onLimitInput)
    input.addEventListener('blur', onLimitBlur)
    input.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') input.blur()
    })
  }

  return finish(sync, () => el.querySelector<HTMLElement>('.ovl-palette-chip[aria-pressed="true"]')?.focus())

  /** Close, placement and the handle, for both modes; `syncColours` fills the colour controls, `focusFirst` focuses one. */
  function finish(syncColours: () => void, focusFirst: () => void): PaletteDialogHandle {
    el.querySelector('.ovl-palette-x')!.addEventListener('click', () => handle.close())
    el.querySelector('.ovl-palette-done')!.addEventListener('click', () => handle.close())

    function place(): void {
      if (!el.isConnected) return
      const a = anchor.getBoundingClientRect()
      // Clear of the whole panel the button sits in, so its other rows stay in view.
      const b = opts.beside?.getBoundingClientRect() ?? a
      const vw = window.innerWidth
      const vh = window.innerHeight
      const w = el.offsetWidth
      const h = el.offsetHeight
      const roomStart = b.left - GAP_PX - EDGE_PX
      const roomEnd = vw - b.right - GAP_PX - EDGE_PX
      let x: number
      let y: number
      if (Math.max(roomStart, roomEnd) >= w) {
        // Beside the button, on the side with more room, its middle level with the button.
        x = roomStart >= roomEnd ? b.left - GAP_PX - w : b.right + GAP_PX
        y = a.top + a.height / 2 - h / 2
      } else {
        // A narrow screen: above the button (below when there is no room above).
        x = a.left + a.width / 2 - w / 2
        y = a.top - GAP_PX - h >= EDGE_PX ? a.top - GAP_PX - h : a.bottom + GAP_PX
      }
      x = Math.max(EDGE_PX, Math.min(vw - w - EDGE_PX, x))
      y = Math.max(EDGE_PX, Math.min(vh - h - EDGE_PX, y))
      el.style.left = `${Math.round(x)}px`
      el.style.top = `${Math.round(y)}px`
    }

    const onDocPointerDown = (ev: PointerEvent): void => {
      const target = ev.target as Node | null
      if (!target || el.contains(target) || anchor.contains(target)) return
      handle.close()
    }
    const onDocKeyDown = (ev: KeyboardEvent): void => {
      if (ev.key !== 'Escape') return
      ev.preventDefault()
      handle.close()
    }
    const onResize = (): void => place()

    const handle: PaletteDialogHandle = {
      element: el,
      get anchor() {
        return anchor
      },
      setAnchor(next, nextRamp) {
        if (nextRamp && ramp) ramp = nextRamp
        if (next !== anchor) {
          anchor = next
          anchor.setAttribute('aria-expanded', 'true')
        }
        syncColours()
        place()
      },
      setOpacity(next) {
        if (alphaHeld || Math.abs(next - opacity) < 1e-6) return
        opacity = next
        syncAlpha()
      },
      close() {
        if (open !== handle) return
        open = null
        document.removeEventListener('pointerdown', onDocPointerDown, true)
        document.removeEventListener('keydown', onDocKeyDown)
        window.removeEventListener('resize', onResize)
        anchor.setAttribute('aria-expanded', 'false')
        const hadFocus = el.contains(document.activeElement)
        el.remove()
        if (hadFocus && anchor.isConnected) anchor.focus()
        opts.onClose?.()
      },
    }

    open = handle
    ;(opts.container ?? document.body).appendChild(el)
    anchor.setAttribute('aria-expanded', 'true')
    syncColours()
    syncAlpha()
    place()
    document.addEventListener('pointerdown', onDocPointerDown, true)
    document.addEventListener('keydown', onDocKeyDown)
    window.addEventListener('resize', onResize)
    if (scale || ramp) focusFirst()
    else (alphaInput ?? el.querySelector<HTMLElement>('.ovl-palette-done'))?.focus()
    return handle
  }
}

/** The palette button's accessible name for a layer. */
export function paletteButtonLabel(title: string): string {
  return t('palette.dialog.open', { title })
}
